import FullCalendar from "@fullcalendar/react";
import themePlugin from "@fullcalendar/react/themes/forma";
import timeGridPlugin from "@fullcalendar/react/timegrid";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween"; // ES 2015
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useMemo } from "react";
import { CommandProjectInfo, CommandWeekDelivery } from "../CommandTypes";

import "@fullcalendar/react/skeleton.css";
import "@fullcalendar/react/themes/forma/palettes/red.css";
import "@fullcalendar/react/themes/forma/theme.css";
import clsx from "clsx";
import { RepeatingCalendarEventDefinition } from "../../models/RepeatingCalendarEventDefinition";

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

interface CommandDayCalendarProps {
    project: CommandProjectInfo;
    data: CommandWeekDelivery[];
    numberOfDays: number;
}

const CommandDayCalendar: React.FC<CommandDayCalendarProps> = ({ project, data, numberOfDays }) => {
    const calendarRef = React.createRef<FullCalendar>();

    const openHoursDefinition = React.useMemo(() => {
        const repeatingCalendarEventDefinitions = project?.repeating_calendar_event_definitions || [];

        const def = repeatingCalendarEventDefinitions?.find((def: any) => def.name === "Project Hours");

        if (def) {
            return RepeatingCalendarEventDefinition.fromApiObject(def, project.timezone);
        }

        return null;
    }, [project]);

    const deliveryHoursAllDay = React.useMemo(() => {
        if (!openHoursDefinition) {
            return true;
        }

        return openHoursDefinition.duration % (24 * 60) === 0;
    }, [openHoursDefinition]);

    const deliveryHoursStart = useMemo(() => {
        if (deliveryHoursAllDay || !openHoursDefinition) {
            return null;
        }

        return dayjs(openHoursDefinition.startAt);
    }, [deliveryHoursAllDay, openHoursDefinition]);

    // numberOfDays is the number of *additional* days beyond today, clamped to
    // between 0 and 2 per the admin-configured setting.
    const totalDays = Math.min(Math.max(numberOfDays, 0), 2) + 1;

    const getColorForEvent = (delivery: CommandWeekDelivery): string => {
        return delivery.color_by || "#FF9E00";
    };

    const findEarliestDeliveryHour = (): Dayjs | null => {
        if (!data || data.length === 0) {
            return null;
        }

        let earliestTime: number | null = null; // Time in minutes since midnight
        let earliestDateTime: Dayjs | null = null;

        data.forEach((delivery) => {
            if (delivery.start_at) {
                const deliveryTime = dayjs(delivery.start_at);
                const timeInMinutes = deliveryTime.hour() * 60 + deliveryTime.minute();

                if (earliestTime === null || timeInMinutes < earliestTime) {
                    earliestTime = timeInMinutes;
                    earliestDateTime = deliveryTime;
                }
            }
        });

        return earliestDateTime;
    };

    const earliestEventScroll = () => {
        let start = dayjs().set("hour", 6).set("minute", 30);

        if (deliveryHoursAllDay) {
            start = dayjs().startOf("day");
        }

        if (deliveryHoursStart && !deliveryHoursAllDay) {
            start = dayjs(deliveryHoursStart);
        }

        const earliestDelivery = findEarliestDeliveryHour();

        if (earliestDelivery && earliestDelivery.isBefore(start)) {
            start = earliestDelivery;
        }

        return start.subtract(2, "hours").format("HH:mm:ss");
    };

    const events = useMemo(() => {
        return data.map((calendarEvent) => {
            const color = getColorForEvent(calendarEvent);
            const isPending = calendarEvent.status === "requested";
            const isDeclined = calendarEvent.status === "declined";

            let classNames = "";
            if (isDeclined) {
                classNames = clsx("fc-event-edge-only", "fc-event-edge-declined");
            } else if (isPending) {
                classNames = clsx("fc-event-edge-only", "fc-event-edge-striped");
            } else {
                classNames = "fc-event-edge-only";
            }

            let title = "";

            title = calendarEvent.name;

            const initials = calendarEvent.receiving_team?.initials;

            if (initials) {
                title = `[${initials}] ${title}`;
            }

            return {
                groupId: initials || "",
                id: calendarEvent.id.toString(),
                className: classNames,
                borderColor: color,
                backgroundColor: `${color.slice(0, 7).padEnd(7, "0")}33`,
                color: `${color.slice(0, 7).padEnd(7, "0")}33`,
                title: title,
                start: new Date(calendarEvent.start_at),
                end: new Date(calendarEvent.end_at),
                object: calendarEvent,
                display: "block",
                baseColor: color, // Store for use in eventDidMount
            };
        });
    }, [data]);

    return (
        <div id="dayCalendar" className="h-full grow">
            <FullCalendar
                ref={calendarRef}
                height="100%"
                plugins={[themePlugin, timeGridPlugin]}
                timeZone={project.timezone}
                initialView="commandCenterDayRange"
                initialDate={dayjs().tz(project.timezone).startOf("day").format("YYYY-MM-DD")}
                rerenderDelay={100}
                events={events}
                slotEventOverlap={false}
                eventContent={(arg) => {
                    const title = arg.event.title;
                    const calendarEvent = arg.event.extendedProps?.object;
                    const isDelivery = calendarEvent?.calendarableType === "Delivery";
                    const duration =
                        arg.event.start && arg.event.end
                            ? dayjs(arg.event.end).diff(dayjs(arg.event.start), "minute")
                            : 0;

                    return (
                        <div className="fc-event-main-frame">
                            {title && <div className="fc-event-title fc-sticky">{title}</div>}
                            {isDelivery && duration > 30 && arg.timeText && (
                                <div className="fc-event-time">{arg.timeText}</div>
                            )}
                        </div>
                    );
                }}
                eventDidMount={(info) => {
                    if (info.event.display === "background" || info.event.display === "inverse-background") return;

                    const baseColor = info.event.extendedProps?.baseColor as string | undefined;
                    if (!baseColor) return;

                    // FullCalendar v7 has removed ability to set different background and border colors, so we have to specify manually here.
                    const bgColor = `${baseColor.slice(0, 7).padEnd(7, "0")}33`;

                    // Direct fallback in case custom classes override FC vars
                    info.el.style.setProperty("border-color", baseColor, "important");
                    info.el.style.setProperty("background-color", bgColor, "important");
                }}
                navLinks
                navLinkWeekClick={() => {}} // Explicitly empty to disable default nav
                headerToolbar={false}
                allDaySlot={false}
                fixedWeekCount={false}
                selectable={false}
                scrollTime={earliestEventScroll()}
                weekends={true}
                views={{
                    commandCenterDayRange: {
                        type: "timeGrid",
                        duration: { days: totalDays },
                    },
                }}
                slotLaneClass="fc-override-lane"
                slotHeaderInnerClass="fc-override-header-inner"
                slotHeaderClass="fc-override-header"
            />
        </div>
    );
};

export default CommandDayCalendar;
